import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import AmvTablePager from '../components/admin/AmvTablePager';
import UiIcon from '../components/UiIcon';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/admin-medicos-visibilidad-premium.css';
import '../styles/farmacia-sucursales-admin.css';

try {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
  });
} catch (error) {}

const ENDPOINTS = {
  list: '/api/atencion-bot/sucursales',
  create: '/api/atencion-bot/sucursales',
  update: (codigo) => `/api/atencion-bot/sucursales/${encodeURIComponent(codigo)}`,
  delete: (codigo) => `/api/atencion-bot/sucursales/${encodeURIComponent(codigo)}`
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const HORARIO_DAYS = [
  { key: 'lu', label: 'Lun' },
  { key: 'ma', label: 'Mar' },
  { key: 'mi', label: 'Mié' },
  { key: 'ju', label: 'Jue' },
  { key: 'vi', label: 'Vie' },
  { key: 'sa', label: 'Sáb' },
  { key: 'do', label: 'Dom' }
];

function showToast({ title, text, icon = 'success' }) {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
    customClass: { popup: 'swal-toast-crm swal-toast-crm--amv' }
  });
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function sortSucursalesByName(list) {
  return [...list].sort((a, b) => {
    const aName = String(a?.nombre || '').trim();
    const bName = String(b?.nombre || '').trim();
    return aName.localeCompare(bName, 'es', { sensitivity: 'base' });
  });
}

function aliasesToText(value) {
  if (!Array.isArray(value)) return '';
  return value.join(', ');
}

function horarioPreviewText(value) {
  const text = String(value || '').trim();
  if (!text) return 'Sin horario visible';
  return text;
}

function BranchMap({ sucursales, selectedCode, onSelect, layoutKey = 'default' }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    mapRef.current = L.map(containerRef.current, {
      center: [-33.02, -68.87],
      zoom: 11,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '' }).addTo(mapRef.current);
    mapRef.current.attributionControl.setPrefix('');
    return () => {
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (error) {}
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    if (layerRef.current) {
      try { layerRef.current.remove(); } catch (error) {}
      layerRef.current = null;
    }

    const withCoords = (sucursales || []).filter(
      (item) => Number.isFinite(Number(item?.latitud)) && Number.isFinite(Number(item?.longitud))
    );

    if (!withCoords.length) {
      mapRef.current.setView([-33.02, -68.87], 11, { animate: false });
      return;
    }

    const group = L.featureGroup();
    withCoords.forEach((item) => {
      const isSelected = String(item.codigo) === String(selectedCode || '');
      const marker = L.marker([Number(item.latitud), Number(item.longitud)], {
        icon: L.divIcon({
          className: 'farmacia-sucursales-map__pin',
          html: `<span class="${isSelected ? 'is-selected' : ''}">${isSelected ? '📍' : '💊'}</span>`,
          iconSize: [28, 28],
          iconAnchor: [14, 28],
          popupAnchor: [0, -24]
        })
      });
      marker.on('click', () => onSelect?.(item.codigo));
      marker.bindPopup(`<strong>${item.nombre}</strong><br/>${item.direccion || ''}`);
      marker.addTo(group);
    });

    group.addTo(mapRef.current);
    layerRef.current = group;

    const selected = withCoords.find((item) => String(item.codigo) === String(selectedCode || ''));
    if (selected) {
      mapRef.current.setView([Number(selected.latitud), Number(selected.longitud)], 14, { animate: false });
      return;
    }

    mapRef.current.fitBounds(group.getBounds(), { padding: [24, 24], animate: false });
  }, [onSelect, selectedCode, sucursales]);

  useEffect(() => {
    if (!mapRef.current) return undefined;

    const invalidate = () => {
      try { mapRef.current?.invalidateSize(); } catch (error) {}
    };

    const firstPass = setTimeout(invalidate, 80);
    const secondPass = setTimeout(invalidate, 260);

    return () => {
      clearTimeout(firstPass);
      clearTimeout(secondPass);
    };
  }, [layoutKey]);

  return <div ref={containerRef} className="farmacia-sucursales-map" aria-label="Mapa de sucursales" />;
}

function BranchLocationPicker({ latitude, longitude, onChange, active = true }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const iconRef = useRef(null);
  const ensureMarkerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    mapRef.current = L.map(containerRef.current, {
      center: [-33.02, -68.87],
      zoom: 11,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '' }).addTo(mapRef.current);
    mapRef.current.attributionControl.setPrefix('');

    iconRef.current = L.divIcon({
      className: 'farmacia-sucursales-picker__pin',
      html: '<span>📍</span>',
      iconSize: [28, 28],
      iconAnchor: [14, 28]
    });

    ensureMarkerRef.current = (lat, lng) => {
      if (!mapRef.current || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
      if (!markerRef.current) {
        markerRef.current = L.marker([lat, lng], { draggable: true, icon: iconRef.current }).addTo(mapRef.current);
        markerRef.current.on('dragend', () => {
          const position = markerRef.current?.getLatLng();
          if (!position) return;
          onChange?.({ latitud: position.lat.toFixed(7), longitud: position.lng.toFixed(7) });
        });
      } else {
        markerRef.current.setLatLng([lat, lng]);
      }
    };

    mapRef.current.on('click', (event) => {
      const lat = Number(event.latlng?.lat);
      const lng = Number(event.latlng?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      ensureMarkerRef.current?.(lat, lng);
      onChange?.({ latitud: lat.toFixed(7), longitud: lng.toFixed(7) });
    });

    return () => {
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (error) {}
        mapRef.current = null;
      }
      markerRef.current = null;
      iconRef.current = null;
      ensureMarkerRef.current = null;
    };
  }, [onChange]);

  useEffect(() => {
    if (!mapRef.current) return;
    const lat = Number(latitude);
    const lng = Number(longitude);
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

    if (hasCoords) {
      ensureMarkerRef.current?.(lat, lng);
      mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 14), { animate: false });
    } else {
      if (markerRef.current) {
        try { mapRef.current.removeLayer(markerRef.current); } catch (error) {}
        markerRef.current = null;
      }
      mapRef.current.setView([-33.02, -68.87], 11, { animate: false });
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (!active || !mapRef.current) return undefined;
    const timeoutId = setTimeout(() => {
      try { mapRef.current?.invalidateSize(); } catch (error) {}
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [active]);

  return (
    <div className="farmacia-sucursales-picker">
      <div className="farmacia-sucursales-picker__meta">
      </div>
      <div ref={containerRef} className="farmacia-sucursales-picker__map" aria-label="Selector de coordenadas" />
    </div>
  );
}

function BranchLocationField({ latitude, longitude, onChange, active = true, onOpenPopup }) {
  return (
    <div className="farmacia-sucursales-admin__map-field-card">
      <div className="farmacia-sucursales-admin__map-field-head">
        <div>
          <strong>Ubicación en mapa</strong>
        </div>
        <button type="button" className="farmacia-sucursales-admin__map-popup-trigger" onClick={onOpenPopup} style={{ marginRight: '20px' }}>
          Abrir mapa
        </button>
      </div>
      <BranchLocationPicker latitude={latitude} longitude={longitude} onChange={onChange} active={active} />
    </div>
  );
}

function createHorarioBlock() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    days: [],
    from: '08:00',
    to: '20:00',
    split: false,
    from2: '17:00',
    to2: '21:00'
  };
}

function formatHorarioDays(days) {
  const ordered = HORARIO_DAYS.filter((item) => days.includes(item.key));
  if (!ordered.length) return '';
  if (ordered.length === 7) return 'Lunes a Domingo';
  const weekdayKeys = HORARIO_DAYS.slice(0, 5).map((item) => item.key);
  const saturdayKey = HORARIO_DAYS[5].key;
  if (weekdayKeys.every((key) => days.includes(key)) && ordered.length === 5) return 'Lunes a Viernes';
  if (weekdayKeys.every((key) => days.includes(key)) && days.includes(saturdayKey) && ordered.length === 6) {
    return 'Lunes a Sábados';
  }
  return ordered.map((item) => item.label).join(', ');
}

function formatHorarioBlocks(blocks) {
  const normalized = (blocks || []).filter((block) => Array.isArray(block.days) && block.days.length && block.from && block.to);
  return normalized
    .map((block) => {
      const dayLabel = formatHorarioDays(block.days);
      const tramoUno = `${block.from} a ${block.to}`;
      const tramoDos = block.split && block.from2 && block.to2 ? ` y de ${block.from2} a ${block.to2}` : '';
      return `${dayLabel} de ${tramoUno}${tramoDos}`;
    })
    .filter(Boolean)
    .join(' · ');
}

function expandHorarioDays(label) {
  const normalized = normalizeText(label);
  if (!normalized) return [];

  if (normalized.includes('lunes a domingo')) {
    return HORARIO_DAYS.map((item) => item.key);
  }
  if (normalized.includes('lunes a sabados')) {
    return HORARIO_DAYS.slice(0, 6).map((item) => item.key);
  }
  if (normalized.includes('lunes a viernes')) {
    return HORARIO_DAYS.slice(0, 5).map((item) => item.key);
  }

  const map = {
    lun: 'lu',
    lunes: 'lu',
    mar: 'ma',
    martes: 'ma',
    mie: 'mi',
    miercoles: 'mi',
    jue: 'ju',
    jueves: 'ju',
    vie: 'vi',
    viernes: 'vi',
    sab: 'sa',
    sabado: 'sa',
    sabados: 'sa',
    dom: 'do',
    domingo: 'do',
    domingos: 'do'
  };

  return [...new Set(
    normalized
      .split(',')
      .map((part) => part.trim())
      .map((part) => map[part] || null)
      .filter(Boolean)
  )];
}

function parseHorarioBlocks(value) {
  const text = String(value || '').trim();
  if (!text) return [createHorarioBlock()];

  const blocks = text
    .split(' · ')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => {
      const match = segment.match(/^(.*?)\s+de\s+(\d{2}:\d{2})\s+a\s+(\d{2}:\d{2})(?:\s+y\s+de\s+(\d{2}:\d{2})\s+a\s+(\d{2}:\d{2}))?$/i);
      if (!match) return null;
      const [, daysLabel, from, to, from2, to2] = match;
      const days = expandHorarioDays(daysLabel);
      if (!days.length) return null;
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        days,
        from,
        to,
        split: Boolean(from2 && to2),
        from2: from2 || '17:00',
        to2: to2 || '21:00'
      };
    })
    .filter(Boolean);

  return blocks.length ? blocks : [createHorarioBlock()];
}

function HorarioComposer({ value, onChange, resetKey }) {
  const [blocks, setBlocks] = useState([createHorarioBlock()]);

  useEffect(() => {
    setBlocks(parseHorarioBlocks(value));
  }, [resetKey, value]);

  function commit(nextBlocks) {
    setBlocks(nextBlocks);
    onChange?.(formatHorarioBlocks(nextBlocks));
  }

  function updateBlock(blockId, patch) {
    commit(blocks.map((block) => (block.id === blockId ? { ...block, ...patch } : block)));
  }

  function toggleDay(blockId, dayKey) {
    commit(
      blocks.map((block) => {
        if (block.id !== blockId) return block;
        const exists = block.days.includes(dayKey);
        return {
          ...block,
          days: exists ? block.days.filter((key) => key !== dayKey) : [...block.days, dayKey]
        };
      })
    );
  }

  function addBlock() {
    commit([...blocks, createHorarioBlock()]);
  }

  function removeBlock(blockId) {
    if (blocks.length <= 1) {
      commit([createHorarioBlock()]);
      return;
    }
    commit(blocks.filter((block) => block.id !== blockId));
  }

  const preview = formatHorarioBlocks(blocks) || String(value || '').trim() || 'Todavía no configuraste un horario visible.';

  return (
    <div className="farmacia-sucursales-admin__schedule-card">
      <div className="farmacia-sucursales-admin__schedule-head">
        <div>
          <strong>Horario visible</strong>
          <span>Armalo por días y franjas. Si una sucursal tiene cortes, sumá un segundo tramo.</span>
        </div>
        <button type="button" className="farmacia-sucursales-admin__ghost" onClick={addBlock}>Agregar franja</button>
      </div>

      <div className="farmacia-sucursales-admin__schedule-preview">
        <span>Vista previa</span>
        <strong>{preview}</strong>
      </div>

      <div className="farmacia-sucursales-admin__schedule-list">
        {blocks.map((block, index) => (
          <div key={block.id} className="farmacia-sucursales-admin__schedule-block">
            <div className="farmacia-sucursales-admin__schedule-block-head">
              <strong>Franja {index + 1}</strong>
              <button type="button" className="farmacia-sucursales-admin__schedule-remove" onClick={() => removeBlock(block.id)}>
                Quitar
              </button>
            </div>

            <div className="farmacia-sucursales-admin__schedule-days">
              {HORARIO_DAYS.map((day) => {
                const active = block.days.includes(day.key);
                return (
                  <button
                    key={day.key}
                    type="button"
                    className={`farmacia-sucursales-admin__schedule-day${active ? ' is-active' : ''}`}
                    onClick={() => toggleDay(block.id, day.key)}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>

            <div className="farmacia-sucursales-admin__schedule-times">
              <label>
                <span>Desde</span>
                <input type="time" value={block.from} onChange={(e) => updateBlock(block.id, { from: e.target.value })} />
              </label>
              <label>
                <span>Hasta</span>
                <input type="time" value={block.to} onChange={(e) => updateBlock(block.id, { to: e.target.value })} />
              </label>
              <button
                type="button"
                className={`farmacia-sucursales-admin__schedule-split${block.split ? ' is-active' : ''}`}
                onClick={() => updateBlock(block.id, { split: !block.split })}
              >
                {block.split ? 'Quitar corte' : 'Agregar corte'}
              </button>
            </div>

            {block.split ? (
              <div className="farmacia-sucursales-admin__schedule-times farmacia-sucursales-admin__schedule-times--split">
                <label>
                  <span>Desde 2</span>
                  <input type="time" value={block.from2} onChange={(e) => updateBlock(block.id, { from2: e.target.value })} />
                </label>
                <label>
                  <span>Hasta 2</span>
                  <input type="time" value={block.to2} onChange={(e) => updateBlock(block.id, { to2: e.target.value })} />
                </label>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function emptyFormState() {
  return {
    codigo: '',
    nombre: '',
    direccion: '',
    localidad: '',
    horario: '',
    telefono: '',
    cadeteria: 0,
    latitud: '',
    longitud: '',
    aliases: ''
  };
}

function formStateFromRow(row) {
  return {
    codigo: String(row?.codigo || ''),
    nombre: String(row?.nombre || ''),
    direccion: String(row?.direccion || ''),
    localidad: String(row?.localidad || ''),
    horario: String(row?.horario || ''),
    telefono: String(row?.telefono || ''),
    cadeteria: Number(row?.cadeteria) === 1 ? 1 : 0,
    latitud: row?.latitud ?? '',
    longitud: row?.longitud ?? '',
    aliases: aliasesToText(row?.aliases_json)
  };
}

function payloadFromRow(row) {
  return {
    codigo: String(row?.codigo || '').trim(),
    nombre: String(row?.nombre || '').trim(),
    direccion: String(row?.direccion || '').trim(),
    localidad: String(row?.localidad || '').trim(),
    horario: String(row?.horario || '').trim(),
    telefono: String(row?.telefono || '').trim(),
    cadeteria: Number(row?.cadeteria) === 1 ? 1 : 0,
    latitud: row?.latitud ?? '',
    longitud: row?.longitud ?? '',
    aliases: Array.isArray(row?.aliases_json) ? row.aliases_json : []
  };
}

function payloadFromForm(form) {
  return {
    codigo: String(form.codigo || '').trim(),
    nombre: String(form.nombre || '').trim(),
    direccion: String(form.direccion || '').trim(),
    localidad: String(form.localidad || '').trim(),
    horario: String(form.horario || '').trim(),
    telefono: String(form.telefono || '').trim(),
    cadeteria: Number(form.cadeteria) === 1 ? 1 : 0,
    latitud: String(form.latitud || '').trim(),
    longitud: String(form.longitud || '').trim(),
    aliases: Array.isArray(form.aliases)
      ? form.aliases.map((part) => part.trim()).filter(Boolean)
      : String(form.aliases || '')
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean)
  };
}

export default function FarmaciaSucursalesAdmin() {
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCode, setSelectedCode] = useState('');
  const [sideCollapsed, setSideCollapsed] = useState(false);
  const [createForm, setCreateForm] = useState(emptyFormState);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyFormState);
  const [editSaving, setEditSaving] = useState(false);
  const [deletingCode, setDeletingCode] = useState('');
  const [cadeteriaSavingCode, setCadeteriaSavingCode] = useState('');
  const [mapPopupMode, setMapPopupMode] = useState('');
  const showHorarioColumn = sideCollapsed;

  const loadSucursales = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.list}`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.sucursales) ? json.sucursales : [];
      const sorted = sortSucursalesByName(list);
      setSucursales(sorted);
      setSelectedCode((current) => current || String(sorted[0]?.codigo || ''));
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de sucursales.');
      setSucursales([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSucursales();
  }, [loadSucursales]);

  const filteredSucursales = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return sucursales;
    return sucursales.filter((item) => {
      const bag = [
        item.codigo,
        item.nombre,
        item.direccion,
        item.localidad,
        item.horario,
        item.telefono,
        ...(Array.isArray(item.aliases_json) ? item.aliases_json : [])
      ];
      return bag.some((value) => normalizeText(value).includes(q));
    });
  }, [search, sucursales]);

  const totalPages = useMemo(() => {
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(filteredSucursales.length / pageSize));
  }, [filteredSucursales.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    if (pageSize <= 0) return filteredSucursales;
    const start = (page - 1) * pageSize;
    return filteredSucursales.slice(start, start + pageSize);
  }, [filteredSucursales, page, pageSize]);

  const selectedSucursal = useMemo(
    () => sucursales.find((item) => String(item.codigo) === String(selectedCode || '')) || filteredSucursales[0] || null,
    [filteredSucursales, selectedCode, sucursales]
  );

  const popupSucursal = mapPopupMode === 'branch' ? selectedSucursal : null;

  useEffect(() => {
    if (!selectedSucursal && sucursales[0]) {
      setSelectedCode(String(sucursales[0].codigo));
    }
  }, [selectedSucursal, sucursales]);

  const withCoordsCount = useMemo(
    () => sucursales.filter((item) => Number.isFinite(Number(item.latitud)) && Number.isFinite(Number(item.longitud))).length,
    [sucursales]
  );

  const rangeStart = filteredSucursales.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, filteredSucursales.length);

  function updateCreateField(field, value) {
    setCreateForm((current) => ({ ...current, [field]: value }));
  }

  function updateCreateCoords(coords) {
    setCreateForm((current) => ({ ...current, ...coords }));
  }

  function updateEditField(field, value) {
    setEditForm((current) => ({ ...current, [field]: value }));
  }

  function updateEditCoords(coords) {
    setEditForm((current) => ({ ...current, ...coords }));
  }

  async function handleCreate(event) {
    event.preventDefault();
    const payload = payloadFromForm(createForm);
    if (!payload.nombre || !payload.direccion) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.create}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const created = json?.sucursal;
      const next = sortSucursalesByName(created ? [...sucursales, created] : sucursales);
      setSucursales(next);
      setSelectedCode(String(created?.codigo || selectedCode || ''));
      setCreateForm(emptyFormState());
      setCreateDrawerOpen(false);
      showToast({ title: 'Sucursal creada', text: created?.nombre || 'La sucursal se agregó correctamente.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo crear', text: err.message || 'Error al crear la sucursal.' });
    } finally {
      setCreating(false);
    }
  }

  function openEdit(row) {
    setEditRow(row);
    setEditForm(formStateFromRow(row));
  }

  function closeEdit() {
    if (editSaving) return;
    setEditRow(null);
    setEditForm(emptyFormState());
    setMapPopupMode('');
  }

  function closeCreateDrawer() {
    if (creating) return;
    setCreateDrawerOpen(false);
    setMapPopupMode('');
  }

  useEffect(() => {
    if (!editRow) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !editSaving) closeEdit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editRow, editSaving]);

  useEffect(() => {
    if (!createDrawerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !creating) closeCreateDrawer();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [createDrawerOpen, creating]);

  async function submitEdit(event) {
    event.preventDefault();
    if (!editRow || editSaving) return;
    const payload = payloadFromForm(editForm);
    if (!payload.codigo || !payload.nombre || !payload.direccion) return;
    setEditSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.update(editRow.codigo)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const updated = json?.sucursal;
      const next = sortSucursalesByName(
        sucursales.map((item) => (String(item.codigo) === String(editRow.codigo) ? updated : item))
      );
      setSucursales(next);
      setSelectedCode(String(updated?.codigo || editRow.codigo));
      closeEdit();
      showToast({ title: 'Sucursal actualizada', text: updated?.nombre || 'Los cambios fueron guardados.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: err.message || 'Error al actualizar la sucursal.' });
    } finally {
      setEditSaving(false);
    }
  }

  async function requestDelete(row) {
    const confirm = await fireConfirmDialog({
      title: 'Eliminar sucursal',
      html: buildCrmSwalBody({
        lead: 'Se quitará esta sucursal del catálogo operativo de Farmacia.',
        details: [
          { label: 'Sucursal', value: row?.nombre || 'Sin nombre' },
          { label: 'Código', value: row?.codigo || 'Sin código' }
        ],
        items: [
          'No se puede eliminar si ya tiene pedidos asociados',
          'Esta acción no se puede deshacer'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      width: 'min(420px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    setDeletingCode(String(row.codigo || ''));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.delete(row.codigo)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const next = sucursales.filter((item) => String(item.codigo) !== String(row.codigo));
      setSucursales(next);
      if (String(selectedCode) === String(row.codigo)) {
        setSelectedCode(String(next[0]?.codigo || ''));
      }
      showToast({ title: 'Sucursal eliminada', text: row?.nombre || 'La sucursal se eliminó correctamente.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo eliminar', text: err.message || 'Error al eliminar la sucursal.' });
    } finally {
      setDeletingCode('');
    }
  }

  async function toggleCadeteria(row) {
    const nextValue = Number(row?.cadeteria) === 1 ? 0 : 1;
    const confirm = await fireConfirmDialog({
      title: nextValue ? 'Activar cadetería' : 'Desactivar cadetería',
      html: buildCrmSwalBody({
        lead: nextValue
          ? 'La sucursal quedará marcada como lista para operar con cadete.'
          : 'La sucursal dejará de quedar marcada como operativa para cadetería.',
        details: [
          { label: 'Sucursal', value: row?.nombre || 'Sin nombre' },
          { label: 'Nuevo estado', value: nextValue ? 'Cadete activo' : 'Cadete inactivo' }
        ],
        items: [
          'Se actualiza al instante en el catálogo operativo',
          'Podés revertirlo cuando quieras desde esta misma tabla'
        ]
      }),
      icon: 'question',
      confirmButtonText: nextValue ? 'Activar' : 'Desactivar',
      cancelButtonText: 'Cancelar',
      width: 'min(440px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    setCadeteriaSavingCode(String(row?.codigo || ''));
    try {
      const payload = { ...payloadFromRow(row), cadeteria: nextValue };
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.update(row.codigo)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const updated = json?.sucursal;
      const next = sortSucursalesByName(
        sucursales.map((item) => (String(item.codigo) === String(row.codigo) ? updated : item))
      );
      setSucursales(next);
      showToast({
        title: nextValue ? 'Cadetería activada' : 'Cadetería desactivada',
        text: updated?.nombre || row?.nombre || 'La sucursal fue actualizada.'
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo actualizar',
        text: err.message || 'Error al cambiar el estado de cadetería.'
      });
    } finally {
      setCadeteriaSavingCode('');
    }
  }

  return (
    <section className="farmacia-sucursales-admin crm-gd-page crm-pl-page crm-pbody">
      <div className="farmacia-sucursales-admin__shell">
        <div className="farmacia-sucursales-admin__top">
          <header className="farmacia-sucursales-admin__hero">
            <div>
              <span className="farmacia-sucursales-admin__eyebrow">Farmacia · Administrador Sucursales</span>
            </div>
            <button type="button" className="farmacia-sucursales-admin__primary farmacia-sucursales-admin__hero-action" onClick={() => setCreateDrawerOpen(true)}>
              <UiIcon name="plus" />
              <span>Agregar sucursal</span>
            </button>
          </header>

          <div className="farmacia-sucursales-admin__stats">
            <article>
              <span>Total</span>
              <strong>{sucursales.length}</strong>
              <small>Sucursales operativas cargadas</small>
            </article>
            <article>
              <span>Geo listas</span>
              <strong>{withCoordsCount}</strong>
              <small>Con latitud y longitud</small>
            </article>
            <article>
              <span>Búsqueda</span>
              <strong>{filteredSucursales.length}</strong>
              <small>Resultados visibles</small>
            </article>
          </div>
        </div>

        <div className="farmacia-sucursales-admin__scroll">
          <div className={`farmacia-sucursales-admin__grid${sideCollapsed ? ' is-side-collapsed' : ''}`}>
        <div className="farmacia-sucursales-admin__main">
          <div className="farmacia-sucursales-admin__panel">
            <div className="farmacia-sucursales-admin__toolbar">
              <div>
                <h2>Listado</h2>
                <p>Buscá por nombre, código, dirección, localidad, teléfono o aliases.</p>
              </div>
              <div className="farmacia-sucursales-admin__toolbar-actions">
                <div className="farmacia-sucursales-admin__search-wrap">
                  <CwListSearch value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar sucursal…" />
                </div>
                <label className="farmacia-sucursales-admin__page-size">
                  <span>Ver</span>
                  <select value={pageSize} onChange={(e) => setPage(Number(e.target.value) || 10)}>
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {error ? (
              <div className="farmacia-sucursales-admin__error">
                <UiIcon name="alert" />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="farmacia-sucursales-admin__table-wrap">
              <table className={`farmacia-sucursales-admin__table${showHorarioColumn ? ' is-side-collapsed' : ''}`}>
                <thead>
                  <tr>
                    <th>Sucursal</th>
                    <th>Ubicación</th>
                    <th>Geo</th>
                    {showHorarioColumn ? <th>Horario</th> : null}
                    <th>Cadete</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={showHorarioColumn ? 6 : 5} className="farmacia-sucursales-admin__empty">
                        <div className="farmacia-sucursales-admin__loading-state" role="status" aria-live="polite">
                          <div className="farmacia-sucursales-admin__loading-badge">
                            <span className="farmacia-sucursales-admin__loading-spinner" aria-hidden="true" />
                            <strong>Sincronizando sucursales</strong>
                          </div>
                          <span>Estamos trayendo el catálogo operativo y preparando la mesa de trabajo.</span>
                          <div className="farmacia-sucursales-admin__loading-skeleton" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={showHorarioColumn ? 6 : 5} className="farmacia-sucursales-admin__empty">
                        <div className="farmacia-sucursales-admin__empty-state" role="status" aria-live="polite">
                          <div className="farmacia-sucursales-admin__empty-icon" aria-hidden="true">
                            <span className="farmacia-sucursales-admin__empty-icon-ring" />
                            <UiIcon name="search" />
                          </div>
                          <div className="farmacia-sucursales-admin__empty-copy">
                            <strong>No hay sucursales para mostrar</strong>
                            <span>
                              {String(search || '').trim()
                                ? 'Probá con otro nombre, código, localidad o alias para volver a poblar el listado.'
                                : 'Cuando cargues o habilites sucursales operativas, las vas a ver aparecer en este listado.'}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : pageItems.map((row) => {
                    const hasCoords = Number.isFinite(Number(row.latitud)) && Number.isFinite(Number(row.longitud));
                    const isSelected = String(selectedCode) === String(row.codigo);
                    const hasCadeteria = Number(row.cadeteria) === 1;
                    return (
                      <tr
                        key={row.codigo}
                        className={isSelected ? 'is-selected' : ''}
                        onClick={() => {
                          setSelectedCode(String(row.codigo));
                          setMapPopupMode('branch');
                        }}
                        aria-selected={isSelected ? 'true' : 'false'}
                      >
                        <td>
                          <div className="farmacia-sucursales-admin__branch-main">
                            <div className="farmacia-sucursales-admin__branch-title-row">
                              <strong>{row.nombre}</strong>
                              <span className="farmacia-sucursales-admin__branch-code">{row.codigo}</span>
                            </div>
                            <span className="farmacia-sucursales-admin__branch-subtitle">Sucursal operativa</span>
                          </div>
                        </td>
                        <td>
                          <div className="farmacia-sucursales-admin__location-cell">
                            <strong>{row.localidad || 'Sin localidad'}</strong>
                            <span>{row.direccion || 'Sin dirección'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="farmacia-sucursales-admin__geo-cell">
                            <span className={`farmacia-sucursales-admin__geo-badge${hasCoords ? ' is-ready' : ''}`}>
                              {hasCoords ? 'Geo lista' : 'Sin geo'}
                            </span>
                            <small>{hasCoords ? 'Mapa disponible' : 'Falta punto'}</small>
                          </div>
                        </td>
                        {showHorarioColumn ? (
                          <td>
                            <div className="farmacia-sucursales-admin__schedule-cell">
                              <span className="farmacia-sucursales-admin__schedule-kicker">Horario visible</span>
                              <strong>{horarioPreviewText(row.horario)}</strong>
                            </div>
                          </td>
                        ) : null}
                        <td>
                          <div className="farmacia-sucursales-admin__cadeteria-cell">
                            <button
                              type="button"
                              className={`farmacia-sucursales-admin__cadeteria-toggle${hasCadeteria ? ' is-on' : ''}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                void toggleCadeteria(row);
                              }}
                              disabled={cadeteriaSavingCode === row.codigo}
                              aria-pressed={hasCadeteria ? 'true' : 'false'}
                              aria-label={`${hasCadeteria ? 'Desactivar' : 'Activar'} cadetería en ${row.nombre}`}
                            >
                              <span className="farmacia-sucursales-admin__cadeteria-track">
                                <span className="farmacia-sucursales-admin__cadeteria-thumb" />
                              </span>
                              <span className="farmacia-sucursales-admin__cadeteria-copy">
                                <strong>{hasCadeteria ? 'Activo' : 'Inactivo'}</strong>
                                <small>{cadeteriaSavingCode === row.codigo ? 'Guardando…' : (hasCadeteria ? 'Listo para cadete' : 'Sin cadete')}</small>
                              </span>
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="farmacia-sucursales-admin__actions">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedCode(String(row.codigo));
                                setMapPopupMode('branch');
                              }}
                              aria-label={`Abrir mapa de ${row.nombre}`}
                            >
                              <UiIcon name="eye" />
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEdit(row);
                              }}
                              aria-label={`Editar ${row.nombre}`}
                            >
                              <UiIcon name="edit" />
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                void requestDelete(row);
                              }}
                              disabled={deletingCode === row.codigo}
                              aria-label={`Eliminar ${row.nombre}`}
                            >
                              <UiIcon name="trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <AmvTablePager
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              filteredCount={filteredSucursales.length}
              totalCount={sucursales.length}
              searchActive={Boolean(String(search || '').trim())}
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setPage}
              entityLabel="sucursales"
            />
          </div>
        </div>

        <aside className={`farmacia-sucursales-admin__side${sideCollapsed ? ' is-collapsed' : ''}`}>
          {sideCollapsed ? (
            <div className="farmacia-sucursales-admin__side-rail">
              <button
                type="button"
                className="farmacia-sucursales-admin__side-rail-toggle"
                onClick={() => setSideCollapsed(false)}
                title="Expandir panel lateral"
                aria-label="Expandir panel lateral"
              >
                <span className="farmacia-sucursales-admin__side-rail-icon" aria-hidden="true">»</span>
                <span className="farmacia-sucursales-admin__side-rail-count">2</span>
              </button>
              <div className="farmacia-sucursales-admin__side-rail-copy">
                <strong>{selectedSucursal?.nombre || 'Panel'}</strong>
                <span>{selectedSucursal?.localidad || 'Mapa y ficha'}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="farmacia-sucursales-admin__panel farmacia-sucursales-admin__panel--map">
                <div className="farmacia-sucursales-admin__panel-head farmacia-sucursales-admin__panel-head--side">
                  <div>
                    <h2>Mapa operativo</h2>
                    <p>Seleccioná una sucursal para centrar el mapa y revisar sus coordenadas.</p>
                  </div>
                  <button
                    type="button"
                    className="farmacia-sucursales-admin__side-toggle"
                    onClick={() => setSideCollapsed(true)}
                    title="Achicar panel lateral"
                    aria-label="Achicar panel lateral"
                  >
                    <span className="farmacia-sucursales-admin__side-toggle-icon" aria-hidden="true">«</span>
                    <span>Achicar</span>
                  </button>
                </div>
                <BranchMap
                  sucursales={filteredSucursales}
                  selectedCode={selectedSucursal?.codigo}
                  onSelect={setSelectedCode}
                  layoutKey={sideCollapsed ? 'collapsed' : 'expanded'}
                />
              </div>

              <div className="farmacia-sucursales-admin__panel farmacia-sucursales-admin__panel--detail">
                <div className="farmacia-sucursales-admin__panel-head">
                </div>
                {selectedSucursal ? (
                  <div className="farmacia-sucursales-admin__detail-card">
                    <div className="farmacia-sucursales-admin__detail-title">
                      <span className="farmacia-sucursales-admin__detail-kicker">Sucursal seleccionada</span>
                      <strong>{selectedSucursal.nombre}</strong>
                      <p>{selectedSucursal.localidad || 'Cobertura operativa activa'}</p>
                    </div>
                    <dl>
                      <div className="is-wide"><dt>Dirección</dt><dd>{selectedSucursal.direccion || 'Sin dato'}</dd></div>
                      <div><dt>Localidad</dt><dd>{selectedSucursal.localidad || 'Sin dato'}</dd></div>
                      <div><dt>Teléfono</dt><dd>{selectedSucursal.telefono || 'Sin dato'}</dd></div>
                      <div><dt>Cadete</dt><dd>{Number(selectedSucursal.cadeteria) === 1 ? 'Activo' : 'Inactivo'}</dd></div>
                      <div><dt>Latitud</dt><dd>{selectedSucursal.latitud ?? 'Sin dato'}</dd></div>
                      <div><dt>Longitud</dt><dd>{selectedSucursal.longitud ?? 'Sin dato'}</dd></div>
                      <div className="is-wide"><dt>Horario</dt><dd>{selectedSucursal.horario || 'Sin dato'}</dd></div>
                      <div className="is-wide"><dt>Aliases</dt><dd>{aliasesToText(selectedSucursal.aliases_json) || 'Sin aliases'}</dd></div>
                    </dl>
                  </div>
                ) : (
                  <div className="farmacia-sucursales-admin__empty-card">Seleccioná una sucursal para ver su ficha.</div>
                )}
              </div>
            </>
          )}
        </aside>
          </div>
        </div>
      </div>

      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--left farmacia-sucursales-admin__drawer-backdrop${
          createDrawerOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'
        }`}
        role="presentation"
        onClick={closeCreateDrawer}
        aria-hidden={!createDrawerOpen}
      >
        <aside
          className={`crm-gd-drawer-panel farmacia-sucursales-admin__drawer${
            createDrawerOpen ? ' crm-gd-drawer-panel--left-open' : ' crm-gd-drawer-panel--left-closed'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="farmacia-sucursales-create-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="farmacia-sucursales-admin__drawer-stripe" aria-hidden="true" />
          <div className="farmacia-sucursales-admin__drawer-head">
            <div>
              <span className="farmacia-sucursales-admin__drawer-eyebrow">Alta rápida</span>
              <h2 id="farmacia-sucursales-create-title">Agregar sucursal</h2>
              <p>Creá una sucursal con su código operativo, ubicación y aliases del bot.</p>
            </div>
            <button type="button" className="farmacia-sucursales-admin__drawer-close" onClick={closeCreateDrawer} aria-label="Cerrar drawer">
              ×
            </button>
          </div>

          <form className="farmacia-sucursales-admin__drawer-form" onSubmit={handleCreate}>
            <div className="farmacia-sucursales-admin__drawer-body">
            <div className="farmacia-sucursales-admin__form-grid farmacia-sucursales-admin__form-grid--drawer">
              <label>
                <span>Código</span>
                <input value={createForm.codigo} onChange={(e) => updateCreateField('codigo', e.target.value)} placeholder="santa_ana_central" />
              </label>
              <label>
                <span>Nombre</span>
                <input value={createForm.nombre} onChange={(e) => updateCreateField('nombre', e.target.value)} placeholder="Farmacia Santa Ana Central" required />
              </label>
              <label className="is-wide">
                <span>Dirección</span>
                <input value={createForm.direccion} onChange={(e) => updateCreateField('direccion', e.target.value)} placeholder="San Martín 951, Luján de Cuyo, Mendoza" required />
              </label>
              <label>
                <span>Localidad</span>
                <input value={createForm.localidad} onChange={(e) => updateCreateField('localidad', e.target.value)} placeholder="Luján de Cuyo" />
              </label>
              <label>
                <span>Teléfono</span>
                <input value={createForm.telefono} onChange={(e) => updateCreateField('telefono', e.target.value)} placeholder="+5492613449407" />
              </label>
              <label>
                <span>Cadetería</span>
                <select value={String(createForm.cadeteria)} onChange={(e) => updateCreateField('cadeteria', Number(e.target.value))}>
                  <option value="0">Inactiva</option>
                  <option value="1">Activa</option>
                </select>
              </label>
              <label>
                <span>Aliases</span>
                <input value={createForm.aliases} onChange={(e) => updateCreateField('aliases', e.target.value)} placeholder="lujan, santa ana central, san martin 951" />
              </label>
              <label className="is-wide">
                <span>Horario</span>
                <HorarioComposer value={createForm.horario} onChange={(value) => updateCreateField('horario', value)} resetKey={`create-${createDrawerOpen ? 'open' : 'closed'}`} />
              </label>
              <div className="is-wide farmacia-sucursales-admin__map-field">
                <BranchLocationField
                  latitude={createForm.latitud}
                  longitude={createForm.longitud}
                  onChange={updateCreateCoords}
                  active={createDrawerOpen}
                  onOpenPopup={() => setMapPopupMode('create')}
                />
              </div>
            </div>
            </div>

            <div className="farmacia-sucursales-admin__drawer-actions">
              <button type="button" className="farmacia-sucursales-admin__ghost" onClick={closeCreateDrawer} disabled={creating}>Cancelar</button>
              <button type="submit" className="farmacia-sucursales-admin__primary" disabled={creating}>
                <UiIcon name="plus" />
                <span>{creating ? 'Guardando…' : 'Agregar sucursal'}</span>
              </button>
            </div>
          </form>
        </aside>
      </div>

      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right farmacia-sucursales-admin__drawer-backdrop${
          editRow ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'
        }`}
        role="presentation"
        onClick={closeEdit}
        aria-hidden={!editRow}
      >
        <aside
          className={`crm-gd-drawer-panel farmacia-sucursales-admin__drawer farmacia-sucursales-admin__drawer--edit${
            editRow ? ' crm-gd-drawer-panel--right-open' : ' crm-gd-drawer-panel--right-closed'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="farmacia-sucursales-edit-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="farmacia-sucursales-admin__drawer-stripe farmacia-sucursales-admin__drawer-stripe--edit" aria-hidden="true" />
          <div className="farmacia-sucursales-admin__drawer-head">
            <div>
              <span className="farmacia-sucursales-admin__drawer-eyebrow">Edición</span>
              <h2 id="farmacia-sucursales-edit-title">{editRow?.nombre || 'Editar sucursal'}</h2>
              <p>Actualizá código, ubicación, contacto y aliases de la sucursal seleccionada.</p>
            </div>
            <button type="button" className="farmacia-sucursales-admin__drawer-close" onClick={closeEdit} aria-label="Cerrar drawer">
              ×
            </button>
          </div>
          <form className="farmacia-sucursales-admin__drawer-form" onSubmit={submitEdit}>
            <div className="farmacia-sucursales-admin__drawer-body">
            <div className="farmacia-sucursales-admin__form-grid farmacia-sucursales-admin__form-grid--drawer">
              <label>
                <span>Código</span>
                <input value={editForm.codigo} onChange={(e) => updateEditField('codigo', e.target.value)} required />
              </label>
              <label>
                <span>Nombre</span>
                <input value={editForm.nombre} onChange={(e) => updateEditField('nombre', e.target.value)} required />
              </label>
              <label className="is-wide">
                <span>Dirección</span>
                <input value={editForm.direccion} onChange={(e) => updateEditField('direccion', e.target.value)} required />
              </label>
              <label>
                <span>Localidad</span>
                <input value={editForm.localidad} onChange={(e) => updateEditField('localidad', e.target.value)} />
              </label>
              <label>
                <span>Teléfono</span>
                <input value={editForm.telefono} onChange={(e) => updateEditField('telefono', e.target.value)} />
              </label>
              <label>
                <span>Cadetería</span>
                <select value={String(editForm.cadeteria)} onChange={(e) => updateEditField('cadeteria', Number(e.target.value))}>
                  <option value="0">Inactiva</option>
                  <option value="1">Activa</option>
                </select>
              </label>
              <label>
                <span>Aliases</span>
                <input value={editForm.aliases} onChange={(e) => updateEditField('aliases', e.target.value)} />
              </label>
              <label className="is-wide">
                <span>Horario</span>
                <HorarioComposer value={editForm.horario} onChange={(value) => updateEditField('horario', value)} resetKey={`edit-${editRow?.codigo || 'none'}`} />
              </label>
              <div className="is-wide farmacia-sucursales-admin__map-field">
                <BranchLocationField
                  latitude={editForm.latitud}
                  longitude={editForm.longitud}
                  onChange={updateEditCoords}
                  active={Boolean(editRow)}
                  onOpenPopup={() => setMapPopupMode('edit')}
                />
              </div>
            </div>
            </div>
            <div className="farmacia-sucursales-admin__drawer-actions">
              <button type="button" className="farmacia-sucursales-admin__ghost" onClick={closeEdit} disabled={editSaving}>Cancelar</button>
              <button type="submit" className="farmacia-sucursales-admin__primary" disabled={editSaving}>
                {editSaving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </aside>
      </div>

      <div
        className={`farmacia-sucursales-admin__map-modal-backdrop${mapPopupMode ? ' is-open' : ''}`}
        role="presentation"
        onClick={() => setMapPopupMode('')}
        aria-hidden={!mapPopupMode}
      >
        <div className="farmacia-sucursales-admin__map-modal" role="dialog" aria-modal="true" aria-labelledby="farmacia-sucursales-map-modal-title" onClick={(e) => e.stopPropagation()}>
          <div className="farmacia-sucursales-admin__map-modal-head">
            <div>
              <span className="farmacia-sucursales-admin__drawer-eyebrow">Ubicación</span>
              <h3 id="farmacia-sucursales-map-modal-title">
                {mapPopupMode === 'branch' ? (popupSucursal?.nombre || 'Sucursal seleccionada') : 'Mapa ampliado'}
              </h3>
              <p>
                {mapPopupMode === 'branch'
                  ? 'La vista se centra en la sucursal elegida y podés revisar su ubicación sin salir del listado.'
                  : 'Elegí el punto directo desde el mapa y cerrá cuando te quede bien ubicado.'}
              </p>
            </div>
            <button type="button" className="farmacia-sucursales-admin__drawer-close" onClick={() => setMapPopupMode('')} aria-label="Cerrar mapa ampliado">
              ×
            </button>
          </div>
          <div className={`farmacia-sucursales-admin__map-modal-body${mapPopupMode === 'branch' ? ' is-branch' : ''}`}>
            {mapPopupMode === 'branch' ? (
              <>
                <div className="farmacia-sucursales-admin__map-modal-stage">
                  <BranchMap
                    sucursales={filteredSucursales}
                    selectedCode={popupSucursal?.codigo}
                    onSelect={setSelectedCode}
                    layoutKey={`branch-${popupSucursal?.codigo || 'none'}`}
                  />
                </div>
                <div className="farmacia-sucursales-admin__map-modal-aside">
                  <div className="farmacia-sucursales-admin__map-modal-card">
                    <span className="farmacia-sucursales-admin__detail-kicker">Sucursal enfocada</span>
                    <strong>{popupSucursal?.nombre || 'Sin sucursal seleccionada'}</strong>
                    <p>{popupSucursal?.direccion || 'Sin dirección registrada'}</p>
                  </div>
                  <div className="farmacia-sucursales-admin__map-modal-card">
                    <span className="farmacia-sucursales-admin__detail-kicker">Estado</span>
                    <strong>{popupSucursal ? (Number(popupSucursal.cadeteria) === 1 ? 'Activo' : 'Inactivo') : 'Sin dato'}</strong>
                    <p>{popupSucursal?.localidad || 'Sin localidad'}</p>
                  </div>
                  <div className="farmacia-sucursales-admin__map-modal-card farmacia-sucursales-admin__map-modal-card--accent">
                    <span className="farmacia-sucursales-admin__detail-kicker">Navegación</span>
                    <strong>Click en cualquier sucursal</strong>
                    <p>El popup cambia el foco a la sucursal elegida y mantiene el contexto sin cerrar.</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {mapPopupMode === 'create' ? (
                  <BranchLocationPicker
                    latitude={createForm.latitud}
                    longitude={createForm.longitud}
                    onChange={updateCreateCoords}
                    active={mapPopupMode === 'create'}
                  />
                ) : null}
                {mapPopupMode === 'edit' ? (
                  <BranchLocationPicker
                    latitude={editForm.latitud}
                    longitude={editForm.longitud}
                    onChange={updateEditCoords}
                    active={mapPopupMode === 'edit'}
                  />
                ) : null}
              </>
            )}
          </div>
          <div className="farmacia-sucursales-admin__map-modal-actions">
            <button type="button" className="farmacia-sucursales-admin__primary" onClick={() => setMapPopupMode('')}>
              Listo
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
